import React, { useState, useEffect } from "react";
import { fetchData } from "../../utils/functions";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import {
  ThinDropdownWithSearch,
  ThinTextField,
} from "../common/GeneralComponent";

const EditHouseholdFinancial = ({ householdFinancial, DqaApproved, DqaRejected }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams();

  const [formData, setFormData] = useState({
    usesFinancialServices: "",
    financialServicesProvidersName: "",
    normalPaymentsMade: "",
    normalPaymentsNotReceived: "",
    serviceIsUseful: "",
    mobileRegisteredOnYourName: "",
    howManyMobilesUsedForFinance: "",
    mainExpenseItems: "",
    usingMobileData: "",
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [financialRecord, setFinancialRecord] = useState(null);
  const [isDqaApproved, setIsDqaApproved] = useState(DqaApproved);
  const [isDqaRejected, setIsDqaRejected] = useState(DqaRejected);
  const [householdInfoData, setHouseholdInfoData] = useState(null);

  // Financial services options
  const financialServicesOptions = [
    { value: "Bank", label: "Bank" },
    { value: "Mobile/Digital Money", label: "Mobile/Digital Money" },
    { value: "No", label: "No" },
  ];

  const yesNoOptions = [
    { value: "1", label: "Yes" },
    { value: "0", label: "No" },
  ];

  // helpers
  const isEmpty = (v) =>
    v === null || v === undefined || String(v).trim() === "";
  const is01 = (v) => v === "0" || v === "1";
  const isNonNegInt = (v) => {
    if (isEmpty(v)) return false;
    const n = Number(v);
    return Number.isInteger(n) && n >= 0;
  };

  const setFieldError = (name, msg) =>
    setErrors((prev) => ({ ...prev, [name]: msg }));
  const clearFieldError = (name) =>
    setErrors((prev) => {
      const next = { ...prev };
      delete next[name];
      return next;
    });

  const safeStringConvert = (value) => {
    if (value === null || value === undefined) return "";
    return String(value);
  };

  const isUsingFinancialServices = (serviceType) => {
    return serviceType === "Bank" || serviceType === "Mobile/Digital Money";
  };

  // Update isDqaApproved when prop changes
  useEffect(() => {
    setIsDqaApproved(DqaApproved);
    setIsDqaRejected(DqaRejected);
  }, [DqaApproved,DqaRejected ]);

  useEffect(() => {
    if (householdFinancial && Object.keys(householdFinancial).length > 0) {
      setFinancialRecord(householdFinancial);

      const mapFieldValue = (snakeCase, camelCase, defaultValue = "") => {
        const snakeCaseValue = householdFinancial[snakeCase];
        const camelCaseValue = householdFinancial[camelCase];
        
        if (snakeCaseValue !== null && snakeCaseValue !== undefined) {
          return safeStringConvert(snakeCaseValue);
        }
        if (camelCaseValue !== null && camelCaseValue !== undefined) {
          return safeStringConvert(camelCaseValue);
        }
        return defaultValue;
      };

      const usesFinancialServices = mapFieldValue("uses_financial_services", "usesFinancialServices");
      
      setFormData({
        usesFinancialServices: usesFinancialServices,
        financialServicesProvidersName: isUsingFinancialServices(usesFinancialServices) 
          ? mapFieldValue("financial_services_providers_name", "financialServicesProvidersName")
          : "",
        normalPaymentsMade: isUsingFinancialServices(usesFinancialServices)
          ? mapFieldValue("normal_payments_made", "normalPaymentsMade")
          : "",
        normalPaymentsNotReceived: isUsingFinancialServices(usesFinancialServices)
          ? mapFieldValue("normal_payments_not_received", "normalPaymentsNotReceived")
          : "",
        serviceIsUseful: isUsingFinancialServices(usesFinancialServices)
          ? mapFieldValue("service_is_useful", "serviceIsUseful")
          : "",
        mobileRegisteredOnYourName: isUsingFinancialServices(usesFinancialServices)
          ? mapFieldValue("mobile_registered_on_your_name", "mobileRegisteredOnYourName")
          : "",
        howManyMobilesUsedForFinance: isUsingFinancialServices(usesFinancialServices)
          ? mapFieldValue("how_many_mobiles_used_for_finance", "howManyMobilesUsedForFinance")
          : "",
        mainExpenseItems: isUsingFinancialServices(usesFinancialServices)
          ? mapFieldValue("main_expense_items", "mainExpenseItems")
          : "",
        usingMobileData: isUsingFinancialServices(usesFinancialServices)
          ? mapFieldValue("using_mobile_data", "usingMobileData")
          : "",
      });
    } else {
      setFormData({
        usesFinancialServices: "",
        financialServicesProvidersName: "",
        normalPaymentsMade: "",
        normalPaymentsNotReceived: "",
        serviceIsUseful: "",
        mobileRegisteredOnYourName: "",
        howManyMobilesUsedForFinance: "",
        mainExpenseItems: "",
        usingMobileData: "",
      });
    }
    setLoading(false);
  }, [householdFinancial]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "usesFinancialServices") {
      if (value === "No") {
        setFormData((prev) => ({
          ...prev,
          usesFinancialServices: "No",
          financialServicesProvidersName: "",
          normalPaymentsMade: "",
          normalPaymentsNotReceived: "",
          serviceIsUseful: "",
          mobileRegisteredOnYourName: "",
          howManyMobilesUsedForFinance: "",
          mainExpenseItems: "",
          usingMobileData: "",
        }));
        // clear related errors too
        setErrors((prev) => {
          const {
            financialServicesProvidersName,
            normalPaymentsMade,
            normalPaymentsNotReceived,
            serviceIsUseful,
            mobileRegisteredOnYourName,
            howManyMobilesUsedForFinance,
            mainExpenseItems,
            usingMobileData,
            ...rest
          } = prev;
          return rest;
        });
      } else {
        setFormData((prev) => ({ ...prev, [name]: value }));
      }
      clearFieldError("usesFinancialServices");
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
    clearFieldError(name);
  };

  const handleDropdownChange = (name, value) => {
    if (name === "usesFinancialServices") {
      if (value === "No") {
        setFormData((prev) => ({
          ...prev,
          usesFinancialServices: "No",
          financialServicesProvidersName: "",
          normalPaymentsMade: "",
          normalPaymentsNotReceived: "",
          serviceIsUseful: "",
          mobileRegisteredOnYourName: "",
          howManyMobilesUsedForFinance: "",
          mainExpenseItems: "",
          usingMobileData: "",
        }));
        // clear related errors too
        setErrors((prev) => {
          const {
            financialServicesProvidersName,
            normalPaymentsMade,
            normalPaymentsNotReceived,
            serviceIsUseful,
            mobileRegisteredOnYourName,
            howManyMobilesUsedForFinance,
            mainExpenseItems,
            usingMobileData,
            ...rest
          } = prev;
          return rest;
        });
      } else {
        setFormData((prev) => ({ ...prev, [name]: value }));
      }
      clearFieldError("usesFinancialServices");
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
    clearFieldError(name);
  };

  const validateAll = () => {
    const next = {};

    // Always require the main choice
    if (!isUsingFinancialServices(formData.usesFinancialServices) && formData.usesFinancialServices !== "No") {
      next.usesFinancialServices = "Please select a financial service option.";
    }

    if (isUsingFinancialServices(formData.usesFinancialServices)) {
      if (isEmpty(formData.financialServicesProvidersName)) {
        next.financialServicesProvidersName = "Provider name is required.";
      }
      if (!is01(formData.serviceIsUseful)) {
        next.serviceIsUseful = "Please select Yes or No.";
      }
      if (!is01(formData.mobileRegisteredOnYourName)) {
        next.mobileRegisteredOnYourName = "Please select Yes or No.";
      }
      if (!is01(formData.usingMobileData)) {
        next.usingMobileData = "Please select Yes or No.";
      }
      if (!isNonNegInt(formData.howManyMobilesUsedForFinance)) {
        next.howManyMobilesUsedForFinance = "Enter a whole number (0 or more).";
      }
      if (isEmpty(formData.normalPaymentsMade)) {
        next.normalPaymentsMade = "Normal Payments Required.";
      }
      if (isEmpty(formData.normalPaymentsNotReceived)) {
        next.normalPaymentsNotReceived = "Normal Payments Not Received Required.";
      }
      // if (isEmpty(formData.mainExpenseItems)) {
      //   next.mainExpenseItems = "Main Expense Items Required.";
      // }
    }

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleUpdate = async () => {
    if (!validateAll()) {
      return;
    }

    try {
      setUpdating(true);

      const isUsingServices = isUsingFinancialServices(formData.usesFinancialServices);

      const payload = {
        householdFinancial: {
          household_information_id: parseInt(id),
          uses_financial_services: formData.usesFinancialServices || null,
          ...(isUsingServices && {
            financial_services_providers_name: formData.financialServicesProvidersName || null,
            normal_payments_made: formData.normalPaymentsMade || null,
            normal_payments_not_received: formData.normalPaymentsNotReceived || null,
            service_is_useful: is01(formData.serviceIsUseful) ? parseInt(formData.serviceIsUseful) : null,
            mobile_registered_on_your_name: is01(formData.mobileRegisteredOnYourName) ? parseInt(formData.mobileRegisteredOnYourName) : null,
            how_many_mobiles_used_for_finance: !isEmpty(formData.howManyMobilesUsedForFinance) ? parseInt(formData.howManyMobilesUsedForFinance) : null,
            main_expense_items: formData.mainExpenseItems || null,
            using_mobile_data: is01(formData.usingMobileData) ? parseInt(formData.usingMobileData) : null,
          }),
 
          ...(!isUsingServices && {
            financial_services_providers_name: null,
            normal_payments_made: null,
            normal_payments_not_received: null,
            service_is_useful: null,
            mobile_registered_on_your_name: null,
            how_many_mobiles_used_for_finance: null,
            main_expense_items: null,
            using_mobile_data: null,
          })
        },
      };

      if (financialRecord && financialRecord.household_financial_services_id) {
        const response = await fetchData(
          `household-financial-update/${financialRecord.household_financial_services_id}`,
          "PUT",
          payload,
          {},
          {},
          navigate,
          "/household",
          dispatch
        );

        if (response?.success) {
          toast.success("Household financial services updated successfully");
        } else {
          throw new Error(
            response?.message || "Failed to update household financial services"
          );
        }
      } else {
        toast.info(
          "No existing financial record found. You may need to create a new financial record."
        );
        return;
      }
    } catch (error) {
      console.error("Error updating household financial services:", error);
      toast.error(
        error.message || "Error updating household financial services"
      );
    } finally {
      setUpdating(false);
    }
  };

  const handleApprove = async () => {
    if (!validateAll()) {
      return;
    }

    try {
      setUpdating(true);

      const approveResponse = await fetchData(
        `householdinformation-approve/${id}`,
        "PUT",
        {},
        {},
        {},
        navigate,
        "/household",
        dispatch
      );

      if (approveResponse?.success) {
        const isUsingServices = isUsingFinancialServices(formData.usesFinancialServices);

        const approveData = {
          householdFinancial: {
            household_information_id: parseInt(id),
            uses_financial_services: formData.usesFinancialServices || null,
    
            ...(isUsingServices && {
              financial_services_providers_name: formData.financialServicesProvidersName || null,
              normal_payments_made: formData.normalPaymentsMade || null,
              normal_payments_not_received: formData.normalPaymentsNotReceived || null,
              service_is_useful: is01(formData.serviceIsUseful) ? parseInt(formData.serviceIsUseful) : null,
              mobile_registered_on_your_name: is01(formData.mobileRegisteredOnYourName) ? parseInt(formData.mobileRegisteredOnYourName) : null,
              how_many_mobiles_used_for_finance: !isEmpty(formData.howManyMobilesUsedForFinance) ? parseInt(formData.howManyMobilesUsedForFinance) : null,
              main_expense_items: formData.mainExpenseItems || null,
              using_mobile_data: is01(formData.usingMobileData) ? parseInt(formData.usingMobileData) : null,
            }),
            ...(!isUsingServices && {
              financial_services_providers_name: null,
              normal_payments_made: null,
              normal_payments_not_received: null,
              service_is_useful: null,
              mobile_registered_on_your_name: null,
              how_many_mobiles_used_for_finance: null,
              main_expense_items: null,
              using_mobile_data: null,
            }),
            status: 1,
          },
        };

        if (
          financialRecord &&
          financialRecord.household_financial_services_id
        ) {
          const response = await fetchData(
            `household-financial-update/${financialRecord.household_financial_services_id}`,
            "PUT",
            approveData,
            {},
            {},
            navigate,
            "/household",
            dispatch
          );

          if (response?.success) {
            toast.success(
              "Household information approved from DQA successfully"
            );
            setIsDqaApproved(true);
          } else {
            throw new Error(
              response?.message ||
                "Failed to update household financial services"
            );
          }
        } else {
          toast.success("Household information approved from DQA successfully");
          setIsDqaApproved(true);
        }
      } else {
        throw new Error(
          approveResponse?.message || "Failed to approve household information"
        );
      }
    } catch (error) {
      console.error("Error approving household financial services:", error);
      toast.error(
        error.message || "Error approving household financial services"
      );
    } finally {
      setUpdating(false);
    }
  };

  // helper to render error text
  const Err = (name) =>
    errors?.[name] ? (
      <p className="text-red-600 text-sm mt-1">{errors[name]}</p>
    ) : null;

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-lg text-gray-600">
          Loading household financial information...
        </div>
      </div>
    );
  }

  const hasFinancialData = householdFinancial && Object.keys(householdFinancial).length > 0;

  return (
    <div className="bg-white rounded">
      <div className="flex justify-between items-center mb-3">
        {isDqaApproved && (
          <>
            <h2 className="text-teal-700 text-xl font-semibold">
              Household Financial Services
            </h2>
            <span className="bg-blue-100 text-blue-800 px-1 py-1 rounded-full text-sm font-semibold">
              Approved from DQA
            </span>
          </>
        )}
        {isDqaRejected && (
          <>
            <h2 className="text-teal-700 text-xl font-semibold">
              Household Financial Services
            </h2>

            <span className="bg-red-100 text-red-800 px-3 py-1 rounded-full text-sm font-semibold">
              Rejected from DQA
            </span>
          </>
        )}
      </div>

      <div className="space-y-6">
        {/* Row 1: Financial Services Question (full width) */}
        <div className="grid grid-cols-1">
          <div>
            <p className="mb-2 text-sm font-medium text-gray-700 whitespace-nowrap">
              Do you use any financial services to manage your income?{" "}
              {!isDqaApproved && !isDqaRejected && <span className="text-red-500">*</span>}
            </p>
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-1">
                <input
                  type="radio"
                  name="usesFinancialServices"
                  value="Bank"
                  checked={formData.usesFinancialServices === "Bank"}
                  onChange={(e) =>
                    handleDropdownChange(
                      "usesFinancialServices",
                      e.target.value,
                    )
                  }
                  className="accent-blue-600"
                  disabled={isDqaApproved || isDqaRejected || !hasFinancialData}
                />
                <span className="text-sm text-gray-700">Bank</span>
              </label>
              <label className="flex items-center gap-1">
                <input
                  type="radio"
                  name="usesFinancialServices"
                  value="Mobile/Digital Money"
                  checked={
                    formData.usesFinancialServices === "Mobile/Digital Money"
                  }
                  onChange={(e) =>
                    handleDropdownChange(
                      "usesFinancialServices",
                      e.target.value,
                    )
                  }
                  className="accent-blue-600"
                  disabled={isDqaApproved || isDqaRejected || !hasFinancialData}
                />
                <span className="text-sm text-gray-700">
                  Mobile/Digital Money
                </span>
              </label>
              <label className="flex items-center gap-1">
                <input
                  type="radio"
                  name="usesFinancialServices"
                  value="No"
                  checked={formData.usesFinancialServices === "No"}
                  onChange={(e) =>
                    handleDropdownChange(
                      "usesFinancialServices",
                      e.target.value,
                    )
                  }
                  className="accent-blue-600"
                  disabled={isDqaApproved || isDqaRejected || !hasFinancialData}
                />
                <span className="text-sm text-gray-700">No</span>
              </label>
            </div>
            {Err("usesFinancialServices")}
          </div>
        </div>

        {isUsingFinancialServices(formData.usesFinancialServices) && (
          <>
            {/* Row 2: First 3 fields */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Financial Services Provider Name */}
              <div>
                <ThinTextField
                  label="Financial Services Provider Name"
                  value={formData.financialServicesProvidersName}
                  onChange={handleChange}
                  name="financialServicesProvidersName"
                  placeholder="Enter provider name"
                  isRequired={true}
                  disabled={isDqaApproved || isDqaRejected || !hasFinancialData}
                />
                {Err("financialServicesProvidersName")}
              </div>

              {/* Normal Payments Made */}
              <div>
                <ThinTextField
                  label="Normal Payments Made"
                  value={formData.normalPaymentsMade}
                  onChange={handleChange}
                  name="normalPaymentsMade"
                  placeholder="Enter payments made"
                  isRequired={true}
                  disabled={isDqaApproved || isDqaRejected || !hasFinancialData}
                />
                {Err("normalPaymentsMade")}
              </div>

              {/* Normal Payments Not Received */}
              <div>
                <ThinTextField
                  label="Normal Payments Not Received"
                  value={formData.normalPaymentsNotReceived}
                  onChange={handleChange}
                  name="normalPaymentsNotReceived"
                  placeholder="Enter payments not received"
                  isRequired={true}
                  disabled={isDqaApproved || isDqaRejected || !hasFinancialData}
                />
                {Err("normalPaymentsNotReceived")}
              </div>
            </div>

            {/* Row 3: Next 3 fields */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Is the service useful? */}
              <div>
                <p className="mb-2 text-sm font-medium text-gray-700">
                  Is the service useful? {!isDqaApproved && !isDqaRejected && <span className="text-red-500">*</span>}
                </p>
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-1">
                    <input
                      type="radio"
                      name="serviceIsUseful"
                      value="1"
                      checked={formData.serviceIsUseful === "1"}
                      onChange={(e) =>
                        handleDropdownChange("serviceIsUseful", e.target.value)
                      }
                      className="accent-blue-600"
                      disabled={isDqaApproved || isDqaRejected || !hasFinancialData}
                    />
                    <span className="text-sm text-gray-700">Yes</span>
                  </label>
                  <label className="flex items-center gap-1">
                    <input
                      type="radio"
                      name="serviceIsUseful"
                      value="0"
                      checked={formData.serviceIsUseful === "0"}
                      onChange={(e) =>
                        handleDropdownChange("serviceIsUseful", e.target.value)
                      }
                      className="accent-blue-600"
                      disabled={isDqaApproved || isDqaRejected || !hasFinancialData}
                    />
                    <span className="text-sm text-gray-700">No</span>
                  </label>
                </div>
                {Err("serviceIsUseful")}
              </div>

              {/* Mobile registered on your name? */}
              <div>
                <p className="mb-2 text-sm font-medium text-gray-700">
                  Mobile registered on your name?{" "}
                  {!isDqaApproved && !isDqaRejected && <span className="text-red-500">*</span>}
                </p>
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-1">
                    <input
                      type="radio"
                      name="mobileRegisteredOnYourName"
                      value="1"
                      checked={formData.mobileRegisteredOnYourName === "1"}
                      onChange={(e) =>
                        handleDropdownChange(
                          "mobileRegisteredOnYourName",
                          e.target.value,
                        )
                      }
                      className="accent-blue-600"
                      disabled={isDqaApproved || isDqaRejected || !hasFinancialData}
                    />
                    <span className="text-sm text-gray-700">Yes</span>
                  </label>
                  <label className="flex items-center gap-1">
                    <input
                      type="radio"
                      name="mobileRegisteredOnYourName"
                      value="0"
                      checked={formData.mobileRegisteredOnYourName === "0"}
                      onChange={(e) =>
                        handleDropdownChange(
                          "mobileRegisteredOnYourName",
                          e.target.value,
                        )
                      }
                      className="accent-blue-600"
                      disabled={isDqaApproved || isDqaRejected || !hasFinancialData}
                    />
                    <span className="text-sm text-gray-700">No</span>
                  </label>
                </div>
                {Err("mobileRegisteredOnYourName")}
              </div>

              {/* How many mobiles used for finance? */}
              <div>
                <ThinTextField
                  label="How many mobiles used for finance?"
                  value={formData.howManyMobilesUsedForFinance}
                  onChange={handleChange}
                  name="howManyMobilesUsedForFinance"
                  placeholder="Enter number"
                  type="number"
                  min="0"
                  isRequired={true}
                  disabled={isDqaApproved || isDqaRejected || !hasFinancialData}
                />
                {Err("howManyMobilesUsedForFinance")}
              </div>
            </div>

            {/* Row 4: Last 2 fields */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Using mobile data? */}
              <div>
                <p className="mb-2 text-sm font-medium text-gray-700">
                  Using mobile data? {!isDqaApproved && !isDqaRejected && <span className="text-red-500">*</span>}
                </p>
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-1">
                    <input
                      type="radio"
                      name="usingMobileData"
                      value="1"
                      checked={formData.usingMobileData === "1"}
                      onChange={(e) =>
                        handleDropdownChange("usingMobileData", e.target.value)
                      }
                      className="accent-blue-600"
                      disabled={isDqaApproved || isDqaRejected || !hasFinancialData}
                    />
                    <span className="text-sm text-gray-700">Yes</span>
                  </label>
                  <label className="flex items-center gap-1">
                    <input
                      type="radio"
                      name="usingMobileData"
                      value="0"
                      checked={formData.usingMobileData === "0"}
                      onChange={(e) =>
                        handleDropdownChange("usingMobileData", e.target.value)
                      }
                      className="accent-blue-600"
                      disabled={isDqaApproved || isDqaRejected || !hasFinancialData}
                    />
                    <span className="text-sm text-gray-700">No</span>
                  </label>
                </div>
                {Err("usingMobileData")}
              </div>

              {/* Main Expense Items */}
              <div>
                <ThinTextField
                  label="Main Expense Items"
                  value={formData.mainExpenseItems}
                  onChange={handleChange}
                  name="mainExpenseItems"
                  placeholder="Enter main expense items"
                  isRequired={false}
                  disabled={isDqaApproved || isDqaRejected || !hasFinancialData}
                />
                {Err("mainExpenseItems")}
              </div>

              {/* Empty div for third column */}
              <div></div>
            </div>
          </>
        )}
      </div>

      {/* Action Buttons */}
      {!isDqaApproved &&
        !isDqaRejected &&
        hasFinancialData &&
        financialRecord && (
          <div className="mt-8 flex justify-end gap-4">
            <button
              type="button"
              onClick={handleUpdate}
              disabled={updating || !financialRecord}
              className={`${
                updating || !financialRecord
                  ? "bg-gray-400"
                  : "bg-teal-600 hover:bg-teal-700"
              } bg-teal-600 text-white rounded-lg px-4 py-2 hover:bg-teal-700 transition-colors`}
            >
              {updating
                ? "Updating..."
                : !financialRecord
                  ? "No Record to Update"
                  : "Update"}
            </button>
            <button
              type="button"
              onClick={handleApprove}
              disabled={updating}
              className={`${
                updating ? "bg-gray-400" : "bg-[#caa316] hover:bg-[#b8920c]"
              } text-white px-4 py-2 rounded-lg transition duration-200 font-medium`}
            >
              {updating ? "Approving..." : "Approve"}
            </button>
          </div>
        )}
    </div>
  );
};

export default EditHouseholdFinancial;